import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PackageX } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import DetailDataCard from '../components/shipments/DetailDataCard';
import DetailHeader from '../components/shipments/DetailHeader';
import MatchSuggestions from '../components/shipments/MatchSuggestions';
import MergeDialog from '../components/shipments/MergeDialog';
import SourceEmails from '../components/shipments/SourceEmails';
import StatusChangeDialog from '../components/shipments/StatusChangeDialog';
import Timeline from '../components/shipments/Timeline';
import { Banner, EmptyState, SkeletonBlock } from '../components/shipments/Ui';
import { useNoticeFromState } from '../components/shipments/useNoticeFromState';
import { useRemote } from '../components/shipments/useRemote';
import { useToasts } from '../components/shipments/useToasts';
import { CODES, shipmentsApi } from '../utils/shipmentsApi';
import { formatDateTime } from '../utils/shipmentsFormat';

// Detail of one shipment: data (inline editing), suggestions, timeline and source e-mails, with the actions
// Upravit / Změnit stav / Sloučit / Znovu zpracovat AI / Smazat (Obnovit for a deleted one).
const ShipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { notify, host } = useToasts();
  useNoticeFromState(notify);

  const detail = useRemote(`d:${id}`, () => shipmentsApi.detail(id));
  const carriers = useRemote('carriers', () => shipmentsApi.carriers());
  const [editId, setEditId] = useState(null);
  const [dialog, setDialog] = useState(null); // status | merge | delete | reprocess
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  // A merged shipment answers 410 MERGED with the id of the shipment it went into: follow it and say so.
  const mergedInto = detail.error?.code === CODES.MERGED ? Number(detail.error.data?.mergedIntoId) : 0;
  useEffect(() => {
    if (mergedInto > 0) {
      navigate(`/zasilky/${mergedInto}`, { replace: true, state: { notice: 'Původní zásilka byla sloučena do této zásilky.', tone: 'info' } });
    }
  }, [mergedInto, navigate]);

  const data = detail.data && String(detail.data.shipment?.id) === String(id) ? detail.data : null;
  const shipment = data?.shipment;
  const deleted = Boolean(shipment?.deletedAt);
  const editMode = editId === id && !deleted;

  const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/zasilky'));
  const patch = async (body) => {
    const next = await shipmentsApi.update(id, body);
    detail.mutate(() => next);
  };
  const unlock = async (field) => {
    await patch({ unlock: [field] });
    notify.success('Pole je odemčené, synchronizace ho může znovu měnit.');
  };

  const perform = async (task, successText) => {
    setBusy(true);
    setActionError('');
    try {
      const result = await task();
      if (successText) notify.success(typeof successText === 'function' ? successText(result) : successText);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (body) => {
    await patch(body);
    setDialog(null);
    notify.success('Stav zásilky byl změněn a zamčen proti synchronizaci.');
  };
  const merge = async (target) => {
    await shipmentsApi.merge(id, target.id);
    navigate(`/zasilky/${Number(target.id)}`, { state: { notice: 'Zásilky byly sloučeny.' } });
  };
  const remove = async () => {
    setDialog(null);
    await perform(async () => {
      await shipmentsApi.remove(id);
      navigate('/zasilky', { state: { notice: 'Zásilka byla smazána. Najdete ji v záložce Smazané a můžete ji obnovit.' } });
    });
  };
  const restore = () => perform(async () => { const next = await shipmentsApi.restore(id); detail.mutate(() => next); }, 'Zásilka byla obnovena.');
  const reprocess = async () => {
    setDialog(null);
    await perform(() => shipmentsApi.reprocess(id), (r) => `Znovu zpracování vyžádáno, e-mailů: ${r?.requested ?? 0}. Proběhne při nejbližším běhu synchronizace.`);
  };

  if (detail.error && detail.error.code !== CODES.MERGED) {
    const notFound = detail.error.code === CODES.NOT_FOUND || detail.error.status === 404;
    return (
      <div className="shp-page">
        <EmptyState
          icon={PackageX} title={notFound ? 'Zásilka nenalezena' : 'Zásilku se nepodařilo načíst'}
          action={<Link to="/zasilky" className="shp-btn">Zpět na seznam zásilek</Link>}
        >
          {notFound ? 'Zásilka neexistuje, nebo byla trvale odstraněna.' : detail.error.message}
        </EmptyState>
      </div>
    );
  }

  if (!shipment) {
    return <div className="shp-page" aria-busy="true"><div className="shp-stack"><SkeletonBlock height={60} /><SkeletonBlock height={320} /></div></div>;
  }

  return (
    <div className="shp-page">
      {host}
      <DetailHeader
        shipment={shipment} deleted={deleted} editMode={editMode} busy={busy} onBack={back}
        onToggleEdit={() => setEditId(editMode ? null : id)} onStatus={() => setDialog('status')} onMerge={() => setDialog('merge')}
        onReprocess={() => setDialog('reprocess')} onDelete={() => setDialog('delete')} onRestore={restore}
      />

      {deleted && (
        <Banner tone="warn">Zásilka je smazaná{shipment.deletedAt ? ` (${formatDateTime(shipment.deletedAt)})` : ''}. Zobrazení je jen pro čtení, upravovat ji jde až po obnovení.</Banner>
      )}
      {actionError && <Banner tone="bad">{actionError}</Banner>}
      {shipment.needsReview && !deleted && (
        <Banner tone="warn">Zásilka čeká na kontrolu. Nejisté e-maily najdete ve frontě v záložce „Ke kontrole“ na seznamu zásilek.</Banner>
      )}

      <div className="shp-detail">
        <div className="shp-stack">
          <MatchSuggestions suggestions={shipment.matchSuggestions} readOnly={deleted} onAssign={patch} />
          <DetailDataCard shipment={shipment} carriers={carriers.data} editMode={editMode} readOnly={deleted} onPatch={patch} onUnlock={unlock} />
        </div>
        <div className="shp-stack">
          <section className="shp-card" aria-labelledby="shp-timeline-title">
            <h2 id="shp-timeline-title" className="shp-h2">Historie</h2>
            <Timeline events={data.events} />
          </section>
          <section className="shp-card" aria-labelledby="shp-emails-title">
            <h2 id="shp-emails-title" className="shp-h2">Zdrojové e-maily ({data.emails?.length || 0})</h2>
            <SourceEmails emails={data.emails} />
          </section>
        </div>
      </div>

      {dialog === 'status' && <StatusChangeDialog current={shipment.status} onClose={() => setDialog(null)} onSubmit={changeStatus} />}
      {dialog === 'merge' && <MergeDialog shipmentId={shipment.id} onClose={() => setDialog(null)} onMerge={merge} />}
      <ConfirmModal
        show={dialog === 'delete'} danger title="Smazat zásilku?" confirmText="Smazat" onCancel={() => setDialog(null)} onConfirm={remove}
        message="Zásilka zmizí ze seznamu. Synchronizace ji nevzkřísí a kdykoli ji můžete obnovit."
      />
      <ConfirmModal
        show={dialog === 'reprocess'} title="Znovu zpracovat AI?" confirmText="Zpracovat znovu" onCancel={() => setDialog(null)} onConfirm={reprocess}
        message="Všechny e-maily této zásilky se pošlou k novému zpracování umělou inteligencí. Zpracování spotřebuje denní limit AI volání."
      />
    </div>
  );
};

export default ShipmentDetail;
